import React, { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { showSuccess, showError } from "@/utils/toast";
import { ChevronDown, ChevronRight, Image as ImageIcon, Download, Trash2, RefreshCw, Loader2, Grid3x3 } from "lucide-react";

interface Library {
    id: number;
    name: string;
    children?: Library[];
    children_count: number;
    total_images_count: number;
    total_slices_count: number;
    unmapped_slices_count: number;
    closed: boolean;
    hidden: boolean;
}

interface ImageItem {
    id: number;
    library_id?: number;
    image_url: string | null;
    description?: string;
    created_at: string;
    updated_at: string;
}

interface LibraryWithImages extends Library {
    images?: ImageItem[];
    imagesLoaded?: boolean;
    imagesLoading?: boolean;
}

const ImageList = forwardRef((_, ref) => {
    const navigate = useNavigate();
    const [libraries, setLibraries] = useState<LibraryWithImages[]>([]);
    const [expandedLibraries, setExpandedLibraries] = useState<Set<number>>(new Set());
    const [loading, setLoading] = useState(true);

    useImperativeHandle(ref, () => ({
        refresh: fetchLibraries,
    }));

    useEffect(() => {
        fetchLibraries();
    }, []);

    const fetchLibraries = async () => {
        setLoading(true);
        try {
            const response = await fetch("/api/admin/libraries");
            if (response.ok) {
                const data = await response.json();
                setLibraries(data.libraries || []);
                setExpandedLibraries(new Set());
            } else {
                showError("Nem sikerült a könyvtárak betöltése.");
            }
        } catch (error) {
            console.error("Hiba a könyvtárak betöltésekor:", error);
            showError("Hiba a könyvtárak betöltésekor.");
        } finally {
            setLoading(false);
        }
    };

    const fetchImagesForLibrary = async (libraryId: number) => {
        try {
            const response = await fetch(`/api/admin/libraries/${libraryId}/images`);
            if (response.ok) {
                const data = await response.json();
                updateLibraryImages(libraryId, data.images || []);
            } else {
                showError("Nem sikerült a képek betöltése.");
            }
        } catch (error) {
            console.error("Hiba a képek betöltésekor:", error);
            showError("Hiba a képek betöltésekor.");
        }
    };

    const updateLibraryImages = (libraryId: number, images: ImageItem[]) => {
        setLibraries((prevLibraries) =>
            prevLibraries.map((lib) => {
                if (lib.id === libraryId) {
                    return { ...lib, images, imagesLoaded: true, imagesLoading: false };
                }
                if (lib.children) {
                    return { ...lib, children: updateLibraryImagesRecursive(lib.children, libraryId, images) };
                }
                return lib;
            })
        );
    };

    const updateLibraryImagesRecursive = (libs: LibraryWithImages[], libraryId: number, images: ImageItem[]): LibraryWithImages[] => {
        return libs.map((lib) => {
            if (lib.id === libraryId) {
                return { ...lib, images, imagesLoaded: true, imagesLoading: false };
            }
            if (lib.children) {
                return { ...lib, children: updateLibraryImagesRecursive(lib.children, libraryId, images) };
            }
            return lib;
        });
    };

    const toggleLibrary = (libraryId: number) => {
        const newExpanded = new Set(expandedLibraries);
        if (newExpanded.has(libraryId)) {
            newExpanded.delete(libraryId);
        } else {
            newExpanded.add(libraryId);
            // Lazy load images when expanding
            setLibraries((prevLibraries) =>
                prevLibraries.map((lib) => {
                    if (lib.id === libraryId && !lib.imagesLoaded) {
                        fetchImagesForLibrary(libraryId);
                        return { ...lib, imagesLoading: true };
                    }
                    if (lib.children) {
                        return { ...lib, children: markLibraryLoadingRecursive(lib.children, libraryId) };
                    }
                    return lib;
                })
            );
        }
        setExpandedLibraries(newExpanded);
    };

    const markLibraryLoadingRecursive = (libs: LibraryWithImages[], libraryId: number): LibraryWithImages[] => {
        return libs.map((lib) => {
            if (lib.id === libraryId && !lib.imagesLoaded) {
                fetchImagesForLibrary(libraryId);
                return { ...lib, imagesLoading: true };
            }
            if (lib.children) {
                return { ...lib, children: markLibraryLoadingRecursive(lib.children, libraryId) };
            }
            return lib;
        });
    };

    const handleDeleteImage = async (imageId: number) => {
        if (!confirm("Biztosan törölni szeretnéd ezt a képet?")) {
            return;
        }

        try {
            const response = await fetch(`/api/admin/images/${imageId}`, {
                method: "DELETE",
            });

            if (response.ok) {
                showSuccess("Kép sikeresen törölve!");
                fetchLibraries();
            } else {
                showError("Hiba a kép törlése során.");
            }
        } catch (error) {
            console.error("Hiba a törlés során:", error);
            showError("Hiba a törlés során.");
        }
    };

    const handleGroupDicing = (library: LibraryWithImages) => {
        navigate(`/admin/dicing/${library.id}`);
    };

    const renderLibraryTree = (libs: LibraryWithImages[], depth = 0): React.ReactNode => {
        return (
            <div className={depth > 0 ? "ml-4 border-l border-gray-200 dark:border-gray-700 pl-4" : ""}>
                {libs.map((library) => (
                    <div key={library.id} className="py-2">
                        {/* Library header */}
                        <div
                            className="flex items-center gap-2 p-2 bg-white dark:bg-slate-800 rounded border border-gray-200 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-700 cursor-pointer"
                            onClick={() => {
                                if (library.children_count > 0 || library.total_images_count > 0) {
                                    toggleLibrary(library.id);
                                }
                            }}
                        >
                            {library.children_count > 0 || library.total_images_count > 0 ? (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        toggleLibrary(library.id);
                                    }}
                                    className="p-1 hover:bg-gray-200 dark:hover:bg-slate-600 rounded"
                                >
                                    {expandedLibraries.has(library.id) ? (
                                        <ChevronDown size={18} />
                                    ) : (
                                        <ChevronRight size={18} />
                                    )}
                                </button>
                            ) : (
                                <div className="w-6" />
                            )}

                            <ImageIcon size={18} className="text-blue-500" />
                            <span className="font-medium flex-1">{library.name}</span>
                            <span className="text-xs text-muted-foreground">
                                {library.total_images_count} kép / {library.total_slices_count - library.unmapped_slices_count}/{library.total_slices_count} slice
                            </span>
                        </div>

                        {/* Images list */}
                        {expandedLibraries.has(library.id) && (
                            <div className="mt-2 space-y-2">
                                {library.imagesLoading ? (
                                    <div className="p-4 text-center text-muted-foreground">
                                        <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
                                        Képek betöltése...
                                    </div>
                                ) : library.images && library.images.length > 0 ? (
                                    <>
                                        <Button
                                            onClick={() => handleGroupDicing(library)}
                                            className="w-full"
                                            variant="default"
                                        >
                                            <Grid3x3 className="mr-2 h-4 w-4" />
                                            Csoportos Darabolás
                                        </Button>
                                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                                            {library.images.map((image) => (
                                                <div key={image.id} className="relative group">
                                                    {image.image_url ? (
                                                        <>
                                                            <img
                                                                src={image.image_url}
                                                                alt="Kép"
                                                                className="w-full h-32 object-cover rounded border border-gray-300 dark:border-slate-600"
                                                            />
                                                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 rounded transition-all flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                                                                <a
                                                                    href={image.image_url}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="p-2 bg-blue-500 hover:bg-blue-600 rounded-full text-white"
                                                                    title="Kép megnyitása"
                                                                >
                                                                    <Download size={16} />
                                                                </a>
                                                                <button
                                                                    onClick={() => handleDeleteImage(image.id)}
                                                                    className="p-2 bg-red-500 hover:bg-red-600 rounded-full text-white"
                                                                    title="Kép törlése"
                                                                >
                                                                    <Trash2 size={16} />
                                                                </button>
                                                            </div>
                                                        </>
                                                    ) : (
                                                        <div className="w-full h-32 bg-gray-200 dark:bg-slate-700 rounded flex items-center justify-center text-muted-foreground">
                                                            Nincs előnézet
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </>
                                ) : (
                                    <p className="text-sm text-muted-foreground italic p-2">Nincsenek képek ebben a könyvtárban.</p>
                                )}
                            </div>
                        )}

                        {/* Recursive children */}
                        {expandedLibraries.has(library.id) && library.children && library.children.length > 0 && (
                            renderLibraryTree(library.children, depth + 1)
                        )}
                    </div>
                ))}
            </div>
        );
    };

    return (
        <React.Fragment>
            <Card>
                <CardHeader>
                    <div className="flex items-center justify-between">
                        <div>
                            <CardTitle>Képek a Könyvtárakban</CardTitle>
                            <CardDescription>
                                Válassza ki a könyvtárat a képek megtekintéséhez
                            </CardDescription>
                        </div>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={fetchLibraries}
                            disabled={loading}
                        >
                            <RefreshCw className="mr-2 h-4 w-4" />
                            Frissítés
                        </Button>
                    </div>
                </CardHeader>
                <CardContent>
                    {loading ? (
                        <p className="text-center text-muted-foreground">Betöltés...</p>
                    ) : libraries.length === 0 ? (
                        <p className="text-center text-muted-foreground">Nincsenek könyvtárak.</p>
                    ) : (
                        renderLibraryTree(libraries)
                    )}
                </CardContent>
            </Card>
        </React.Fragment>
    );
});

ImageList.displayName = "ImageList";

export default ImageList;
